<!--
	Copyright (c) 2017-present, flop! contributors
	SPDX-License-Identifier: AGPL-3.0-or-later
	https://framagit.org/flopedt/flopedt
-->

<script lang="ts">
export interface ButtonProps {
  color?: ButtonColor
  look?: ButtonLook
  iconButton?: boolean
  disabled?: boolean
  type?: 'button' | 'submit' | 'reset'
}

export type ButtonEmits = {
  click: [e: MouseEvent]
}

export enum ButtonColor {
  PRIMARY = 'primary',
  ACCENT = 'accent',
  WARNING = 'warning',
  DANGER = 'danger',
  SUCCESS = 'success',
}

export enum ButtonLook {
  FILLED = 'filled',
  OUTLINED = 'outlined',
  LINK = 'link',
}
</script>

<script setup lang="ts">
const props = withDefaults(defineProps<ButtonProps>(), {
  color: ButtonColor.PRIMARY,
  look: ButtonLook.FILLED,
  type: 'button',
})

const emit = defineEmits<ButtonEmits>()
defineSlots<{ default(props: {}): unknown }>()
</script>

<template>
  <button
    :type="type"
    :disabled="disabled"
    :class="[
      'flopui-button',
      `flopui-button__color-${props.color}`,
      `flopui-button__look-${props.look}`,
      { 'flopui-button-icon': iconButton },
    ]"
    @click="!disabled && emit('click', $event)"
  >
    <slot />
  </button>
</template>

<!--suppress CssUnusedSymbol-->
<style>
.flopui-button {
  all: unset;
  box-sizing: border-box;
  min-height: 0;
  min-width: 0;
  flex-shrink: 0;
  cursor: pointer;
}

.flopui-button:not(.flopui-button__look-link) {
  position: relative;

  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;

  font-size: 0.6875rem;
  font-weight: 500;
  text-transform: uppercase;
  border: 1px transparent solid;
  border-radius: var(--border-radius);
  padding: 0 0.625rem;
  height: 2rem;

  transition:
    background-color 0.2s,
    color 0.2s,
    filter 0.2s;
}

.flopui-button.flopui-button-icon {
  width: 2rem;
  padding: 0;
}

.flopui-button:focus-visible {
  outline: var(--color-outline) solid 2px;
}

.flopui-button:is(:disabled, [aria-disabled]) {
  cursor: not-allowed;
  opacity: 0.65;
}

.flopui-button :where(svg, .flopui-loader) {
  width: 1rem;
  height: 1rem;
  color: inherit;
  fill: currentColor;
}

.flopui-button__look-filled:not(:disabled, [aria-disabled]):hover {
  filter: brightness(0.9);
}

.flopui-button__look-filled.flopui-button__color-primary {
  background-color: var(--color-background-primary);
  border-color: var(--color-border-primary);
  color: var(--color-text-primary);
}

.flopui-button__look-filled.flopui-button__color-accent {
  background-color: var(--color-background-accent);
  color: var(--color-text-accent-contrast);
}

.flopui-button__look-filled.flopui-button__color-warning {
  background-color: var(--color-background-warning);
  color: var(--color-text-warning-contrast);
}

.flopui-button__look-filled.flopui-button__color-danger {
  background-color: var(--color-background-danger);
  color: var(--color-text-danger-contrast);
}

.flopui-button__look-filled.flopui-button__color-success {
  background-color: var(--color-background-success);
  color: var(--color-text-success-contrast);
}

.flopui-button__look-outlined.flopui-button__color-primary {
  border-color: var(--color-border-primary);
  background-color: var(--color-background-primary);
  color: var(--color-text-primary);
}

.flopui-button__look-outlined.flopui-button__color-accent {
  border-color: var(--color-border-accent);
  background-color: var(--color-background-accent-light);
  color: var(--color-text-accent);
}

.flopui-button__look-outlined.flopui-button__color-warning {
  border-color: var(--color-border-warning);
  background-color: var(--color-background-warning-light);
  color: var(--color-text-primary);
}

.flopui-button__look-outlined.flopui-button__color-danger {
  border-color: var(--color-border-danger);
  background-color: var(--color-background-danger-light);
  color: var(--color-text-primary);
}

.flopui-button__look-outlined.flopui-button__color-success {
  border-color: var(--color-border-success);
  background-color: var(--color-background-success-light);
  color: var(--color-text-primary);
}

.flopui-button.flopui-button__look-link {
  font-weight: 500;
  color: #646cff;
  text-decoration: underline;
}

.flopui-button.flopui-button__look-link:hover {
  color: #535bf2;
}
</style>
